import { Component, useEffect, useRef, useState } from 'react';
import type { ErrorInfo, ReactNode } from 'react';

import { useStack9 } from '@april9au/stack9-react';
import { Icon } from '@april9au/stack9-ui';
import {
  Card,
  Col,
  Row,
  Statistic,
  Select,
  Button,
  Spin,
  Tooltip,
  Typography,
} from 'antd';
import dayjs, { type Dayjs } from 'antd/node_modules/dayjs';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip as ChartTooltip,
  ResponsiveContainer,
} from 'recharts';

import DayjsDatePicker from '../components/DayjsDatePicker';

const { Title } = Typography;

const ALL_USER_TYPES = ['Employer', 'Worker', 'Service Provider'];
const BRAND_BLUE = '#247bc7';

type GraphPoint = { bucket: string | number; submissions: number };

type CsvRow = {
  id: string;
  claimNumber: string;
  customerName: string;
  userType: string;
  submittedAt: string;
  comments: string;
  attachmentTypes: string;
};

type ReportData = {
  graphData: GraphPoint[];
  thisMonthCount: number;
  lastMonthCount: number;
};

function toIsoDateString(date: Date): string {
  return date.toISOString();
}

function monthStart(offset = 0): Date {
  const d = new Date();
  return new Date(d.getFullYear(), d.getMonth() + offset, 1);
}

function monthEnd(offset = 0): Date {
  const d = new Date();
  return new Date(
    d.getFullYear(),
    d.getMonth() + offset + 1,
    0,
    23,
    59,
    59,
    999,
  );
}

const filterLabelStyle: React.CSSProperties = {
  fontSize: 11,
  fontWeight: 600,
  color: '#666',
  textTransform: 'uppercase',
  letterSpacing: '0.4px',
  marginBottom: 4,
};

class SubmissionReportErrorBoundary extends Component<
  { children: ReactNode },
  { error: Error | null }
> {
  constructor(props: { children: ReactNode }) {
    super(props);
    this.state = { error: null };
  }

  static getDerivedStateFromError(error: Error) {
    return { error };
  }

  componentDidCatch(error: Error, info: ErrorInfo) {
    console.error('[SubmissionReport] render error:', error, info);
  }

  render() {
    if (this.state.error) {
      return (
        <div style={{ padding: 24, color: 'red' }}>
          <strong>Failed to render Submission Report:</strong>
          <pre style={{ marginTop: 8, whiteSpace: 'pre-wrap' }}>
            {(this.state.error as Error).message}
          </pre>
        </div>
      );
    }
    return this.props.children;
  }
}

const SubmissionReportInner = () => {
  const { entityService, queryService } = useStack9();

  const [selectionRange, setSelectionRange] = useState({
    startDate: monthStart(),
    endDate: monthEnd(),
  });
  const [groupDataBy, setGroupDataBy] = useState<
    'hour' | 'day' | 'month' | 'year'
  >('day');
  const [groupByUserType, setGroupByUserType] =
    useState<string[]>(ALL_USER_TYPES);
  const [groupByAttachmentType, setGroupByAttachmentType] = useState<string[]>(
    [],
  );
  const [allAttachmentTypes, setAllAttachmentTypes] = useState<string[]>([]);
  const [graphData, setGraphData] = useState<GraphPoint[]>([]);
  const [submissionsThisMonth, setSubmissionsThisMonth] = useState(0);
  const [submissionsLastMonth, setSubmissionsLastMonth] = useState(0);
  const [totalSubmissions, setTotalSubmissions] = useState(0);
  const [appliedLabel, setAppliedLabel] = useState('');
  const [loading, setLoading] = useState(false);
  const [exporting, setExporting] = useState(false);
  const abortRef = useRef(false);

  async function runQuery(vars: {
    dateFrom: string;
    dateTo: string;
    groupBy: string;
    userTypes: string[];
    attachmentTypes: string[];
  }): Promise<ReportData> {
    const res = await entityService.postWebhook<ReportData>(
      'claim_form_submission',
      'submission-report/data',
      vars,
    );
    return res.data;
  }

  useEffect(() => {
    async function initialLoad() {
      const { startDate, endDate } = selectionRange;
      const endOfDay = new Date(endDate);
      endOfDay.setHours(23, 59, 59, 999);

      // Fetch attachment types from the entity (all types, not date-range scoped)
      // and report data in parallel
      const [attTypesRes, thisRes, lastRes, reportRes] =
        await Promise.allSettled([
          queryService.runNamedQuery<{ results: { name: string }[] }>(
            'submission_report',
            'getAttachmentTypes',
            { vars: { page: 0, limit: 200 } },
          ),
          runQuery({
            dateFrom: toIsoDateString(monthStart(0)),
            dateTo: toIsoDateString(monthEnd(0)),
            groupBy: 'month',
            userTypes: ALL_USER_TYPES,
            attachmentTypes: [],
          }),
          runQuery({
            dateFrom: toIsoDateString(monthStart(-1)),
            dateTo: toIsoDateString(monthEnd(-1)),
            groupBy: 'month',
            userTypes: ALL_USER_TYPES,
            attachmentTypes: [],
          }),
          runQuery({
            dateFrom: toIsoDateString(startDate),
            dateTo: toIsoDateString(endOfDay),
            groupBy: groupDataBy,
            userTypes: ALL_USER_TYPES,
            attachmentTypes: [],
          }),
        ]);

      if (attTypesRes.status === 'fulfilled') {
        const allTypes = (attTypesRes.value.data?.results ?? [])
          .map(r => r.name)
          .sort((a, b) => a.localeCompare(b));
        setAllAttachmentTypes(allTypes);
        setGroupByAttachmentType(allTypes);
      }

      if (thisRes.status === 'fulfilled')
        setSubmissionsThisMonth(thisRes.value.thisMonthCount);
      if (lastRes.status === 'fulfilled')
        setSubmissionsLastMonth(lastRes.value.lastMonthCount);
      if (reportRes.status === 'fulfilled') {
        const data = reportRes.value;
        setGraphData(data.graphData ?? []);
        setTotalSubmissions(
          data.graphData?.reduce((s, p) => s + p.submissions, 0) ?? 0,
        );
        setAppliedLabel(
          `${dayjs(startDate).format('DD/MM/YYYY')} – ${dayjs(endOfDay).format(
            'DD/MM/YYYY',
          )} · Grouped by ${
            groupDataBy.charAt(0).toUpperCase() + groupDataBy.slice(1)
          }`,
        );
      }
    }
    initialLoad().catch(console.error);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  async function fetchData() {
    abortRef.current = false;
    setLoading(true);
    try {
      const { startDate, endDate } = selectionRange;
      const endOfDay = new Date(endDate);
      endOfDay.setHours(23, 59, 59, 999);

      const data = await runQuery({
        dateFrom: toIsoDateString(startDate),
        dateTo: toIsoDateString(endOfDay),
        groupBy: groupDataBy,
        userTypes: groupByUserType,
        attachmentTypes: groupByAttachmentType,
      });

      if (abortRef.current) return;

      setGraphData(data.graphData ?? []);
      setTotalSubmissions(
        data.graphData?.reduce((s, p) => s + p.submissions, 0) ?? 0,
      );
      setAppliedLabel(
        `${dayjs(startDate).format('DD/MM/YYYY')} – ${dayjs(endOfDay).format(
          'DD/MM/YYYY',
        )} · Grouped by ${
          groupDataBy.charAt(0).toUpperCase() + groupDataBy.slice(1)
        }`,
      );
    } catch (err) {
      console.error('Failed to fetch submission report:', err);
    } finally {
      if (!abortRef.current) setLoading(false);
    }
  }

  async function exportCsv() {
    setExporting(true);
    try {
      const { startDate, endDate } = selectionRange;
      const endOfDay = new Date(endDate);
      endOfDay.setHours(23, 59, 59, 999);

      const res = await entityService.postWebhook<CsvRow[]>(
        'claim_form_submission',
        'submission-report/export-csv',
        {
          dateFrom: toIsoDateString(startDate),
          dateTo: toIsoDateString(endOfDay),
          userTypes: groupByUserType,
          attachmentTypes: groupByAttachmentType,
        },
      );

      const rows: CsvRow[] = Array.isArray(res.data) ? res.data : [];
      const headers = [
        'ID',
        'Claim Number',
        'Customer Name',
        'User Type',
        'Submitted At',
        'Comments',
        'Attachment Types',
      ];
      const csvContent = [
        headers.join(','),
        ...rows.map(r =>
          [
            r.id,
            r.claimNumber,
            r.customerName,
            r.userType,
            r.submittedAt,
            r.comments,
            r.attachmentTypes,
          ]
            .map(v => `"${String(v ?? '').replace(/"/g, '""')}"`)
            .join(','),
        ),
      ].join('\n');

      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'Submission Report.csv';
      a.click();
      URL.revokeObjectURL(url);
    } catch (err) {
      console.error('Failed to export CSV:', err);
    } finally {
      setExporting(false);
    }
  }

  return (
    <div style={{ padding: '24px' }}>
      <Title level={2}>Submission Report</Title>

      {/* Controls + filters card */}
      <Card
        size="small"
        style={{ marginBottom: 24, background: '#fafafa' }}
        bodyStyle={{ padding: '16px' }}
      >
        <div style={{ display: 'flex', gap: 16, alignItems: 'stretch' }}>
          {/* Left 2/3 — all filters */}
          <div className="flex flex-col gap-6 w-full">
            {/* Row 1: date range + group by */}
            <div style={{ display: 'flex', gap: 12, alignItems: 'flex-end' }}>
              <div>
                <div style={filterLabelStyle}>Date Range</div>
                <DayjsDatePicker.RangePicker
                  value={[
                    dayjs(selectionRange.startDate),
                    dayjs(selectionRange.endDate),
                  ]}
                  format="DD/MM/YYYY"
                  placeholder={['Start date', 'End date']}
                  onChange={(dates: [Dayjs | null, Dayjs | null] | null) => {
                    if (!dates?.[0] || !dates?.[1]) return;
                    setSelectionRange({
                      startDate: dates[0].toDate(),
                      endDate: dates[1].endOf('day').toDate(),
                    });
                  }}
                />
              </div>
              <div>
                <div style={filterLabelStyle}>Group By</div>
                <Select
                  value={groupDataBy}
                  onChange={setGroupDataBy}
                  style={{ minWidth: 110 }}
                  options={(['hour', 'day', 'month', 'year'] as const).map(
                    g => ({
                      label: g.charAt(0).toUpperCase() + g.slice(1),
                      value: g,
                    }),
                  )}
                />
              </div>
            </div>

            {/* Row 2: user type + attachment type full-width selects */}
            <div style={{ flex: 1 }}>
              <div style={filterLabelStyle}>User Type</div>
              <Select
                mode="multiple"
                allowClear
                placeholder="All user types"
                value={groupByUserType}
                onChange={setGroupByUserType}
                options={ALL_USER_TYPES.map(t => ({ label: t, value: t }))}
                style={{ width: '100%' }}
                maxTagCount="responsive"
              />
            </div>

            <div style={{ flex: 1 }}>
              <div style={filterLabelStyle}>Attachment Type</div>
              <Select
                mode="multiple"
                allowClear
                placeholder={
                  allAttachmentTypes.length === 0
                    ? 'Loading...'
                    : 'All attachment types'
                }
                value={groupByAttachmentType}
                onChange={setGroupByAttachmentType}
                options={allAttachmentTypes.map(t => ({
                  label: t,
                  value: t,
                }))}
                style={{ width: '100%' }}
                maxTagCount="responsive"
                disabled={allAttachmentTypes.length === 0}
              />
            </div>
          </div>

          {/* Right 1/3 — stacked action buttons */}
          <div className="flex flex-col gap-3 items-start justify-start w-48">
            <Button
              type="primary"
              onClick={() => {
                fetchData().catch(console.error);
              }}
              loading={loading}
              block
            >
              Apply Filters
            </Button>
            <Button
              icon={<Icon icon="ExportOutlined" />}
              onClick={() => {
                exportCsv().catch(console.error);
              }}
              loading={exporting}
              block
            >
              Export CSV
            </Button>
          </div>
        </div>
      </Card>

      {/* Metric cards */}
      {(() => {
        const now = new Date();
        const monthName = now.toLocaleString('default', { month: 'long' });
        const lastMonthName = new Date(
          now.getFullYear(),
          now.getMonth() - 1,
          1,
        ).toLocaleString('default', { month: 'long' });
        const year = now.getFullYear();
        const cards = [
          {
            label: (
              <span>
                Total Submissions{' '}
                <Tooltip title="Total for the selected date range and active filters">
                  <span style={{ cursor: 'help' }}>
                    <Icon icon="Info" style={{ fontSize: 13, color: '#999' }} />
                  </span>
                </Tooltip>
              </span>
            ),
            value: totalSubmissions,
          },
          {
            label: `Submissions this month (${monthName} ${year})`,
            value: submissionsThisMonth,
          },
          {
            label: `Submissions last month (${lastMonthName} ${year})`,
            value: submissionsLastMonth,
          },
        ];
        return (
          <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
            {cards.map(({ label, value }, i) => (
              <Col key={i} xs={24} sm={8}>
                <Card>
                  <div
                    style={{ display: 'flex', alignItems: 'center', gap: 16 }}
                  >
                    <div
                      style={{
                        borderRadius: '50%',
                        width: 48,
                        height: 48,
                        background: BRAND_BLUE,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        color: '#fff',
                        fontSize: 20,
                        flexShrink: 0,
                      }}
                    >
                      <Icon icon="FileTextTwoTone" />
                    </div>
                    <Statistic title={label} value={value} />
                  </div>
                </Card>
              </Col>
            ))}
          </Row>
        );
      })()}

      {/* Bar chart */}
      <Card
        title={
          <div>
            <div>Submissions over time</div>
            {appliedLabel && (
              <div
                style={{
                  fontSize: 12,
                  fontWeight: 400,
                  color: '#888',
                  marginTop: 2,
                }}
              >
                {appliedLabel}
              </div>
            )}
          </div>
        }
        extra={
          <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
            <div
              style={{
                width: 12,
                height: 12,
                background: BRAND_BLUE,
                borderRadius: 2,
              }}
            />
            <span style={{ fontSize: 12, color: '#666' }}>Submissions</span>
          </div>
        }
      >
        <Spin spinning={loading}>
          <ResponsiveContainer width="100%" height={400}>
            <BarChart
              data={graphData}
              margin={{ top: 20, right: 16, left: 0, bottom: 20 }}
            >
              <XAxis dataKey="bucket" tick={{ fontSize: 12 }} />
              <YAxis
                allowDecimals={false}
                tick={{ fontSize: 12 }}
                label={{
                  value: 'Number of Submissions',
                  position: 'insideLeft',
                  angle: -90,
                  offset: 20,
                  style: { fontSize: 12 },
                }}
              />
              <ChartTooltip cursor={{ fill: '#e6f0f8' }} />
              <Bar
                dataKey="submissions"
                name="Submissions"
                fill={BRAND_BLUE}
                barSize={18}
                radius={[3, 3, 0, 0]}
              />
            </BarChart>
          </ResponsiveContainer>
        </Spin>
        <div
          style={{
            textAlign: 'center',
            marginTop: 4,
            fontSize: 12,
            color: '#888',
          }}
        >
          Date of Submission
        </div>
      </Card>
    </div>
  );
};

export const SubmissionReport = () => (
  <SubmissionReportErrorBoundary>
    <SubmissionReportInner />
  </SubmissionReportErrorBoundary>
);
